'use client';

import { useRef, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { MobileBottomSheet, BottomSheetHeader } from '@/_components/MobileBottomSheet';
import { UserAvatar } from '@/_components/UserAvatar';

const getDisplayName = (user) => {
    if (user?.firstName || user?.lastName) {
        return [user.firstName, user.lastName].filter(Boolean).join(' ');
    }
    if (user?.name && user.name !== user.email) return user.name;
    return null;
};

export const Avatar = ({ user, size = 32, className = '' }) => (
    <UserAvatar
        firstName={user?.firstName}
        lastName={user?.lastName}
        name={user?.name}
        email={user?.email}
        image={user?.profileImage}
        size={size}
        className={`cursor-pointer ${className}`}
    />
);

const accountMenuItems = [
    { label: 'My Account',    icon: 'pi pi-user',         url: '/account',           hint: 'Profile & account overview' },
    { label: 'My Orders',     icon: 'pi pi-shopping-bag', url: '/account/orders',    hint: 'Track & view your orders' },
    { label: 'My Support',    icon: 'pi pi-headphones',   url: '/account/support',   hint: 'Tickets, warranty & help' },
    { label: 'Saved Address', icon: 'pi pi-map-marker',   url: '/account/addresses', hint: 'Manage delivery addresses' },
    { label: 'Settings',      icon: 'pi pi-cog',          url: '/account/settings',  hint: 'Password & preferences' },
];

/* ─── Shared button shell: icon on top, label below ─── */
const NavBtn = ({ children, label, active, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const color = active || hovered ? 'var(--primary-color)' : '#6b7280';

    return (
        <div
            className="flex flex-column align-items-center justify-content-stretch gap-1 px-2 md:px-3 py-2 border-round-lg cursor-pointer transition-all transition-duration-200"
            style={{
                minWidth: '3.5rem',
                userSelect: 'none',
                background: hovered ? 'var(--primary-50)' : 'transparent',
            }}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={{height:'1.75rem'}}>{typeof children === 'function' ? children(color) : children}  </div>
            <div
                style={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'rgb(107, 114, 128)',
                    transition: 'color 0.2s',
                    lineHeight: 1,
                    marginTop: 2,
                }}
            >
                {label}
            </div>
        </div>
    );
};

const SECURITY_ITEMS = [
    { icon: 'pi pi-credit-card', title: 'No card data stored',         body: 'We never save your card number, CVV, or expiry date on our servers. Payment details are handled exclusively by our payment gateway.' },
    { icon: 'pi pi-server',      title: 'Managed by payment gateway',  body: 'All transactions are processed end-to-end by a PCI-DSS certified payment gateway. Your sensitive data is encrypted and never touches our systems.' },
    { icon: 'pi pi-eye-slash',   title: 'We only receive confirmation', body: 'After a successful payment we receive only an order confirmation token — no card details, no bank information, no UPI IDs.' },
    { icon: 'pi pi-verified',    title: 'Industry-standard compliance', body: 'Our payment partners comply with PCI-DSS Level 1 — the highest security standard in the payments industry.' },
];

const SecurityContent = () => (
    <div style={{ paddingTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 20 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-lock" style={{ fontSize: '1.2rem', color: '#16a34a' }} />
            </div>
            <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: '#14532d' }}>Your payments are 100% secure</p>
                <p style={{ margin: '3px 0 0', fontSize: '0.72rem', color: '#166534', lineHeight: 1.4 }}>We never store your card or UPI details</p>
            </div>
        </div>
        {SECURITY_ITEMS.map(({ icon, title, body }) => (
            <div key={title} style={{ display: 'flex', gap: 14, paddingBottom: 18, marginBottom: 18, borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={icon} style={{ fontSize: '1rem', color: 'var(--primary-color)' }} />
                </div>
                <div>
                    <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{title}</p>
                    <p style={{ margin: 0, fontSize: '0.73rem', color: '#64748b', lineHeight: 1.55 }}>{body}</p>
                </div>
            </div>
        ))}
    </div>
);

const DesktopMenuItem = ({ item, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: 11,
                width: '100%', padding: '9px 10px', borderRadius: 10,
                background: hovered ? 'rgba(11,49,99,0.06)' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                transition: 'background 0.15s',
            }}
        >
            <div style={{
                width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                background: hovered ? 'rgba(11,49,99,0.12)' : '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s',
            }}>
                <i className={item.icon} style={{ fontSize: '0.88rem', color: '#0b3163' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>{item.label}</p>
                {item.hint && <p style={{ margin: '2px 0 0', fontSize: '0.66rem', color: '#94a3b8', lineHeight: 1.2 }}>{item.hint}</p>}
            </div>
            <i className="pi pi-angle-right" style={{ fontSize: '0.7rem', color: hovered ? '#0b3163' : '#cbd5e1', flexShrink: 0, transition: 'color 0.15s' }} />
        </button>
    );
};

export const UserMenu = ({ isLoggedIn, authUser, onNavigate, onLogout, loading = false }) => {
    const panelRef  = useRef(null);
    const pathname  = usePathname();
    const active    = pathname?.startsWith('/account');

    const [isMobile,          setIsMobile]          = useState(false);
    const [sheetOpen,         setSheetOpen]         = useState(false);
    const [securitySheetOpen, setSecuritySheetOpen] = useState(false);
    const [securityModalOpen, setSecurityModalOpen] = useState(false);

    const openSecurity = () => {
        if (typeof window !== 'undefined' && window.innerWidth >= 768) {
            setSecurityModalOpen(true);
        } else {
            setSecuritySheetOpen(true);
        }
    };

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    if (loading) {
        return (
            <div className="flex flex-column align-items-center gap-1 px-2" >
                <Skeleton shape="circle" size="2.6rem" />
                <Skeleton width="1.8rem" height="0.5rem" borderRadius="4px" />
            </div>
        );
    }

    if (!isLoggedIn) {
        return (
            <NavBtn label="Login" active={false} onClick={() => onNavigate('/login')}>
                {(color) => (
                    <i className="pi pi-user" style={{ fontSize: '1.35rem', color, transition: 'color 0.2s' }} />
                )}
            </NavBtn>
        );
    }

    const displayName = getDisplayName(authUser);
    const close       = () => setSheetOpen(false);

    return (
        <>
            <NavBtn
                label="Account"
                active={active}
                onClick={(e) => isMobile ? setSheetOpen(true) : panelRef.current.toggle(e)}
            >
                {() => <Avatar user={authUser} size={25} />}
            </NavBtn>

            {/* ── Desktop: OverlayPanel ────────────────────────────────────── */}
            <OverlayPanel ref={panelRef} className="p-0" style={{ minWidth: '300px', borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 32px rgba(11,49,99,0.16), 0 2px 8px rgba(0,0,0,0.08)' }}>

                {/* ── User header ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #0b3163 0%, #1a5276 100%)',
                    padding: '18px 18px 16px',
                    borderRadius: 14,
                    margin: 8,
                }}>
                    <style>{`
                        @keyframes ennix-spin {
                            from { transform: rotate(0deg); }
                            to   { transform: rotate(360deg); }
                        }
                        .ennix-avatar-ring {
                            position: relative;
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                        }
                        .ennix-avatar-ring::before {
                            content: '';
                            position: absolute;
                            inset: -4px;
                            border-radius: 50%;
                            border: 2.5px solid transparent;
                            border-top-color: rgba(255,255,255,0.9);
                            border-right-color: rgba(255,255,255,0.4);
                            animation: ennix-spin 1.8s linear infinite;
                        }
                        .ennix-avatar-ring::after {
                            content: '';
                            position: absolute;
                            inset: -8px;
                            border-radius: 50%;
                            border: 1.5px solid transparent;
                            border-bottom-color: rgba(255,255,255,0.35);
                            border-left-color: rgba(255,255,255,0.15);
                            animation: ennix-spin 3.2s linear infinite reverse;
                        }
                    `}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="ennix-avatar-ring" style={{ flexShrink: 0 }}>
                            <Avatar user={authUser} size={46} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {displayName || 'My Account'}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {authUser?.email}
                            </p>
                        </div>
                        <button
                            onClick={() => { panelRef.current.hide(); onNavigate('/account'); }}
                            style={{
                                flexShrink: 0, padding: '5px 12px', borderRadius: 20,
                                background: 'rgba(255,255,255,0.15)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                color: '#fff', fontSize: '0.68rem', fontWeight: 600,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                        >
                            View Profile
                        </button>
                    </div>
                </div>


                {/* ── Quick access row ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '8px 10px 4px' }}>
                    {[
                        { label: 'My Account', icon: 'pi pi-user',         url: '/account' },
                        { label: 'My Orders',  icon: 'pi pi-shopping-bag', url: '/account/orders' },
                    ].map((item) => (
                        <button
                            key={item.label}
                            onClick={() => { panelRef.current.hide(); onNavigate(item.url); }}
                            style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                gap: 5, padding: '9px 8px',
                                background: '#f8fafc', border: '1.5px solid #e2e8f0',
                                borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = 'rgba(11,49,99,0.25)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                            <i className={item.icon} style={{ fontSize: '1rem', color: '#0b3163' }} />
                            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1e293b' }}>{item.label}</span>
                        </button>
                    ))}
                </div>

                {/* ── Profile warning ── */}
                {!displayName && (
                    <div
                        onClick={() => { panelRef.current.hide(); onNavigate('/account/profile'); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '9px 16px', cursor: 'pointer',
                            background: '#fffbeb', borderBottom: '1px solid #fde68a',
                        }}
                    >
                        <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', fontSize: '0.78rem', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#92400e', flex: 1 }}>
                            Please update your name on your profile
                        </span>
                        <i className="pi pi-arrow-right" style={{ color: '#d97706', fontSize: '0.62rem', flexShrink: 0 }} />
                    </div>
                )}

                {/* ── Menu items ── */}
                <div style={{ padding: '8px 10px' }}>
                    {accountMenuItems.map((item) => (
                        <DesktopMenuItem
                            key={item.label}
                            item={item}
                            onClick={() => { panelRef.current.hide(); onNavigate(item.url); }}
                        />
                    ))}
                </div>

                {/* ── Logout ── */}
                <div style={{ padding: '0 10px 10px' }}>
                    <button
                        onClick={() => { panelRef.current.hide(); onLogout(); }}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            width: '100%', padding: '10px',
                            marginTop: 4,
                            background: '#0b3163', border: 'none', borderRadius: 10,
                            cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#0d3d7a'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#0b3163'; }}
                    >
                        <i className="pi pi-sign-out" style={{ fontSize: '0.85rem', color: '#fff' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>Logout</span>
                    </button>
                </div>
            </OverlayPanel>

            {/* ── Mobile: main account bottom sheet ───────────────────────── */}
            <MobileBottomSheet visible={sheetOpen} onHide={close}>
                <BottomSheetHeader onClose={close}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar user={authUser} size={40} />
                        <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {displayName || 'My Account'}
                            </p>
                            <p style={{ margin: 0, fontSize: '0.64rem', color: 'rgba(255,255,255,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {authUser?.email}
                            </p>
                        </div>
                    </div>
                </BottomSheetHeader>

                <div style={{ flex: 1, overflowY: 'auto', background: '#ffffff' }}>

                    {/* ── Quick access ─────────────────────────────────────── */}
                    <div style={{ display: 'flex', gap: 10, padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
                        {[
                            { label: 'My Orders',   icon: 'pi pi-shopping-bag', url: '/account/orders' },
                            { label: 'My Address',  icon: 'pi pi-map-marker',   url: '/account/addresses' },
                            { label: 'Support',     icon: 'pi pi-headphones',   url: '/account/support' },
                        ].map((item) => (
                            <button
                                key={item.label}
                                type="button"
                                onClick={() => { close(); onNavigate(item.url); }}
                                style={{
                                    flex: 1, display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', gap: 7,
                                    padding: '12px 6px',
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 12, cursor: 'pointer',
                                }}
                            >
                                <i className={item.icon} style={{ fontSize: '1.15rem', color: 'var(--primary-color)' }} />
                                <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#374151', textAlign: 'center', lineHeight: 1.2 }}>
                                    {item.label}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* ── Security notice ──────────────────────────────────── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px',
                        background: '#f0fdf4',
                        borderBottom: '1px solid #bbf7d0',
                    }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: '#dcfce7',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="pi pi-shield" style={{ fontSize: '0.95rem', color: '#16a34a' }} />
                        </div>
                        <span style={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, color: '#166534', lineHeight: 1.4 }}>
                            We do not store any payment related information
                        </span>
                        <button
                            type="button"
                            onClick={openSecurity}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '5px 11px',
                                background: '#16a34a', color: '#fff',
                                border: 'none', borderRadius: 6,
                                fontSize: '0.65rem', fontWeight: 700,
                                cursor: 'pointer', flexShrink: 0,
                            }}
                        >
                            <i className="pi pi-shield" style={{ fontSize: '0.6rem' }} />
                            View Details
                        </button>
                    </div>

                    {/* Profile warning */}
                    {!displayName && (
                        <div
                            onClick={() => { close(); onNavigate('/account/profile'); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '12px 18px', cursor: 'pointer',
                                background: '#fffbeb', borderBottom: '1px solid #fde68a',
                            }}
                        >
                            <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', fontSize: '0.85rem', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#92400e', flex: 1 }}>
                                Please update your name on your profile
                            </span>
                            <i className="pi pi-arrow-right" style={{ color: '#d97706', fontSize: '0.65rem', flexShrink: 0 }} />
                        </div>
                    )}

                    {/* Menu items */}
                    {accountMenuItems.map((item, idx) => (
                        <button
                            key={item.label}
                            onClick={() => { close(); onNavigate(item.url); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 14,
                                width: '100%', padding: '15px 18px',
                                background: 'transparent', border: 'none',
                                borderBottom: idx < accountMenuItems.length - 1 ? '1px solid #f3f4f6' : 'none',
                                cursor: 'pointer', textAlign: 'left',
                            }}
                        >
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                background: '#f1f5f9',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <i className={item.icon} style={{ fontSize: '1rem', color: 'var(--primary-color)' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#111827', lineHeight: 1.3 }}>
                                    {item.label}
                                </div>
                                {item.hint && (
                                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2, lineHeight: 1.3 }}>
                                        {item.hint}
                                    </div>
                                )}
                            </div>
                            <i className="pi pi-angle-right" style={{ fontSize: '0.8rem', color: '#9ca3af', flexShrink: 0 }} />
                        </button>
                    ))}

                    {/* Logout */}
                    <button
                        onClick={() => { close(); onLogout(); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 14,
                            width: '100%', padding: '15px 18px',
                            background: 'transparent', border: 'none',
                            borderTop: '1px solid #f3f4f6',
                            cursor: 'pointer', textAlign: 'left',
                        }}
                    >
                        <div style={{
                            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                            background: '#fef2f2',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="pi pi-sign-out" style={{ fontSize: '1rem', color: '#ef4444' }} />
                        </div>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#ef4444', flex: 1 }}>
                            Logout
                        </span>
                    </button>
                </div>
            </MobileBottomSheet>

            {/* ── Payment security — desktop modal ── */}
            <Dialog
                visible={securityModalOpen}
                onHide={() => setSecurityModalOpen(false)}
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="pi pi-shield" style={{ fontSize: '0.9rem', color: '#16a34a' }} />
                        </div>
                        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>Payment Security</span>
                    </div>
                }
                style={{ width: '460px' }}
                breakpoints={{ '768px': '95vw' }}
                modal
                draggable={false}
                resizable={false}
                pt={{ content: { style: { padding: '0 18px 18px' } } }}
            >
                <SecurityContent />
            </Dialog>

            {/* ── Payment security info sheet (mobile bottom sheet) ── */}
            <MobileBottomSheet visible={securitySheetOpen} onHide={() => setSecuritySheetOpen(false)}>
                <BottomSheetHeader onClose={() => setSecuritySheetOpen(false)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <i className="pi pi-shield" style={{ color: '#4ade80', fontSize: '1.1rem' }} />
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>Payment Security</span>
                    </div>
                </BottomSheetHeader>
                <div style={{ padding: '20px 18px', background: '#fff', overflowY: 'auto' }}>
                    <SecurityContent />
                </div>
            </MobileBottomSheet>
        </>
    );
};
